import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getOAuthProviderMeta } from '../data/oauthProvidersMeta';
import {
  clearOAuthPending,
  loadOAuthPending,
} from '../utils/oauthConnectionFlow';

const ConnectionsOAuthCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState('working');
  const [message, setMessage] = useState('Completing OAuth connection…');

  const token = localStorage.getItem('token');
  const API = process.env.REACT_APP_BACKEND_URL;

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Please sign in, then try connecting again.');
        return;
      }

      const oauthError = params.get('error');
      if (oauthError) {
        clearOAuthPending();
        setStatus('error');
        setMessage(params.get('error_description') || oauthError);
        return;
      }

      const code = params.get('code');
      const returnedState = params.get('state');
      const pending = loadOAuthPending();

      if (!code) {
        setStatus('error');
        setMessage('Missing authorization code from provider.');
        return;
      }
      if (!pending) {
        setStatus('error');
        setMessage('OAuth session expired. Go back to Connections and start again.');
        return;
      }
      if (pending.state && returnedState && pending.state !== returnedState) {
        clearOAuthPending();
        setStatus('error');
        setMessage('OAuth state mismatch — possible CSRF. Please try again.');
        return;
      }

      try {
        const res = await fetch(
          `${API}/api/connections/oauth2/${pending.providerId}/exchange`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              client_id: pending.clientId,
              client_secret: pending.clientSecret,
              code,
              redirect_uri: pending.redirectUri || `${window.location.origin}/connections/oauth/callback`,
              name: pending.name,
              state: returnedState || pending.state || '',
            }),
          },
        );
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(data?.detail || `HTTP ${res.status}`);
        }

        clearOAuthPending();
        const label = getOAuthProviderMeta(pending.providerId).label;
        setStatus('ok');
        setMessage(`${label} connected successfully`);
        toast.success(`${label} connected`);
        setTimeout(() => {
          navigate(`/connections?connected=${pending.providerId}`, { replace: true });
        }, 800);
      } catch (e) {
        clearOAuthPending();
        setStatus('error');
        setMessage(e.message || 'Token exchange failed');
        toast.error(e.message || 'OAuth failed');
      }
    };

    run();
  }, [API, headers, navigate, params, token]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-6" data-testid="oauth-callback">
      <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center">
        {status === 'working' && (
          <>
            <Loader2 className="w-10 h-10 text-violet-400 animate-spin mx-auto mb-4" />
            <h1 className="text-lg font-semibold mb-2">Connecting…</h1>
            <p className="text-sm text-zinc-400">{message}</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-lg font-semibold mb-2">Connected</h1>
            <p className="text-sm text-zinc-400">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
            <h1 className="text-lg font-semibold mb-2">Connection failed</h1>
            <p className="text-sm text-zinc-400 mb-4">{message}</p>
            <button
              type="button"
              onClick={() => navigate('/connections', { replace: true })}
              className="text-sm text-violet-300 hover:text-violet-200"
            >
              Back to Connections
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ConnectionsOAuthCallback;
